import { Middleware } from '@reduxjs/toolkit';
import { cartActions } from '../reducers/cartSlice';
import { selectIsAIAutoMode } from '../redux/features/ai/ai.selector';
import { draftCartActions } from '../redux/features/draftCart/draftCart.slice';
import {
  selectAIUpsellSuppression,
  selectIsExpressUpsellSuppressionActive,
} from '../selectors/menu';
import { AIUpsellSuppression } from '../utils/cart.upsellConditions';
import logger from '../utils/logger';
import { RootState, RootStore } from './store';

/**
 * Express mode applies to the order only once the agent is driving it. While
 * the AI is in auto mode the order cart mirrors the AI's hypothesis — every
 * frame clears and rebuilds it — so writing a decline into it would be wiped,
 * and it is not the agent's to change. After the takeover the lines the AI put
 * there, `addedBy: AI` included, are the agent's order and what the copilot
 * reads back as `agent_cart`.
 */
export const isExpressSuppressionActiveForOrder = (state: RootState) =>
  selectIsExpressUpsellSuppressionActive(state) && !selectIsAIAutoMode(state);

const NO_AI_SUPPRESSION: AIUpsellSuppression = {
  capExceeded: false,
  extremeExpress: false,
};

/** HITL-1799: the AI's decline-count rules, on the order only once the agent drives. */
export const selectAIUpsellSuppressionForOrder = (
  state: RootState
): AIUpsellSuppression =>
  selectIsAIAutoMode(state)
    ? NO_AI_SUPPRESSION
    : selectAIUpsellSuppression(state);

export const hasAIUpsellSuppression = (suppression: AIUpsellSuppression) =>
  suppression.capExceeded || suppression.extremeExpress;

// The rules that are on in `after` but were off in `before`.
const turnedOn = (
  before: AIUpsellSuppression,
  after: AIUpsellSuppression
): AIUpsellSuppression => ({
  capExceeded: after.capExceeded && !before.capExceeded,
  extremeExpress: after.extremeExpress && !before.extremeExpress,
});

/**
 * HITL-1806 — when express suppression turns on mid-order, decline the open
 * `do_not_upsell_in_express_mode` prompts on the items already in the cart.
 *
 * The add-time decline only covers items added after express mode is on (and
 * skips AI-imported lines), so an item already in the cart when the express
 * timer elapsed or the express meal period became active kept its tagged prompt
 * open — and in copilot mode the AI reads that open prompt off the cart and
 * keeps suggesting it.
 *
 * Two edges, each fired once per transition and never on its own dispatch:
 *   - the order cart, when "express on AND agent driving" turns true — express
 *     turning on after the takeover, or the takeover while express is on;
 *   - the draft cart (the agent's own, never rebuilt by the AI), when express
 *     turns on.
 * The takeover is followed by an async intercept import that clears and
 * rebuilds the order cart, which `useHypothesisFrames` covers by declining again
 * once that import settles.
 */
const expressSuppressionMiddleware: Middleware =
  (store: RootStore) => (next) => (action) => {
    const before = store.getState();
    const result = next(action);
    const after = store.getState();
    if (
      !isExpressSuppressionActiveForOrder(before) &&
      isExpressSuppressionActiveForOrder(after)
    ) {
      logger.debug(
        `[EXPRESS_MODE] on for the agent's order (${action?.type}): declining open tagged prompts on the cart`
      );
      store.dispatch(cartActions.declineExpressSuppressedGroups());
    }
    if (
      !selectIsExpressUpsellSuppressionActive(before) &&
      selectIsExpressUpsellSuppressionActive(after)
    ) {
      store.dispatch(draftCartActions.declineExpressSuppressedGroups());
    }
    // HITL-1799: the same two edges for the AI's decline-count rules, per rule,
    // so a rule already in force never re-declines what the agent re-opened.
    // AI express needs no edge here: it is part of the express selector above.
    const orderTurnedOn = turnedOn(
      selectAIUpsellSuppressionForOrder(before),
      selectAIUpsellSuppressionForOrder(after)
    );
    if (hasAIUpsellSuppression(orderTurnedOn)) {
      logger.debug(
        `[AI_UPSELL_SUPPRESSION] on for the agent's order (${
          action?.type
        }): ${JSON.stringify(orderTurnedOn)}`
      );
      store.dispatch(cartActions.declineAISuppressedGroups(orderTurnedOn));
    }
    const draftTurnedOn = turnedOn(
      selectAIUpsellSuppression(before),
      selectAIUpsellSuppression(after)
    );
    if (hasAIUpsellSuppression(draftTurnedOn)) {
      store.dispatch(draftCartActions.declineAISuppressedGroups(draftTurnedOn));
    }
    return result;
  };

export default expressSuppressionMiddleware;
